/**
 * @file SearchInvoices.js
 * @version 2.29.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-09-23
 * @modified 2026-11-03
 *
 * 2.29.0 (2026-11-03, Cam — "split one invoice across dates or move it after
 * it has been invoiced … from … search invoices table context menu"): "Book
 * over…" in the row menu and right-click (BookOverInvoiceFlow), greyed with the
 * server's reason (`book_over_block` — paid, a till document, made in Xero …);
 * the list is read again after a booking.
 *
 * 2.28.2 (2026-10-28, Cam — rep mobile review): the search and Type filter
 * groups carry classes (inv-filter-search, inv-filter-type) so a rep's phone
 * lays the filters out as Sales Orders does (RepMobile.css).
 *
 * 2.28.1 (2026-10-21, Cam): the export's line Description column is named
 * Sales description, as the expanded lines now are (InvoiceTable 1.13.0);
 * the item code stays a column of its own there.
 *
 * 2.28.0 (2026-10-08): "Add Credit" and "Add Debit" beside Allocate Credit in
 * every row's menu (utils/invoiceAdjustment) — a credit note or invoice
 * pre-filled from the invoice, linked to it and (a credit) allocated to it on
 * save. Disabled with the reason on credit notes, till, voided and deleted
 * rows, for view-only accounts and without "Create invoices". Replaces the old
 * "Add Debit", which carried only the contact and reference.
 *
 * 2.27.0 (2026-10-07): "Change customer…" in every row's menu — live on a till
 * invoice or credit note, disabled with its reason on every other row
 * (ChangeCustomerModal; services/tillCustomerChangeService on the server).
 *
 * 2.26.0 (2026-10-05): Edit on a VOIDED invoice or credit note opens the
 * voided window (VoidedDocumentDialog): it cannot be edited or restored, here
 * or in Xero — raise a copy, or email accounts.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { API_URL } from '../config';
import './SearchLayout.css';
import './SearchInvoices.css';
import Dropdown from './Dropdown';
import LastXeroSync from './LastXeroSync';
import Input from './Input';
import DateInput from './DateInput';
import DateRangeFilter from './DateRangeFilter';
import ProductRangeFilter, { productRangeParam } from './ProductRangeFilter';
import ContactModal from './ContactModal';
import PaginationControls from './PaginationControls';
import ReactDOM from 'react-dom';
import InvoiceTable from './InvoiceTable';
import VoidedDocumentDialog from './VoidedDocumentDialog';
import { isVoided } from '../utils/voidedDocument';
import ExportTableButton from './ExportTableButton';
import InvoiceModal from './InvoiceModal';
import ChangeCustomerModal from './ChangeCustomerModal';
import BookOverInvoiceFlow from './BookOverInvoiceFlow';
import { describeCustomerChange } from '../utils/customerChange';
import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import LoadingBar from './LoadingBar';
import DocumentOutputModal from './DocumentOutputModal';
import AllocateCreditNoteModal from './AllocateCreditNoteModal';
import { useSlideOutButton } from './SlideOutButton';
import { useDragSelect } from '../hooks/useDragSelect';
import TickCheckbox from './TickCheckbox';
import usePersistedState from '../hooks/usePersistedState';
import RepMobileFilterReset from './RepMobileFilterReset';
import { useReveal, revealFilters } from '../utils/revealInSearch';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import { formatPrice } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { useAccess, useReadOnly } from '../hooks/useAccess';
import { ADJUST_ACTIONS, startAdjustment, adjustActionBlock } from '../utils/invoiceAdjustment';

// The most rows an export will fetch for "all results" — the export route's own cap.
const EXPORT_ALL_LIMIT = 1000;

/*
 * What Clear Filters resets to — and so what "no filters applied" means for
 * the button's highlight and the toolbar pill (utils/activeFilters.js).
 */
const CLEARED_FILTERS = {
  search: '', status: 'all', type: 'all', productRange: [], datePreset: 'all-time',
  dateFrom: '', dateTo: '', page: 1, limit: '20',
  showVoid: false, includeAdditives: true
};

function SearchInvoices({ user, onNavigate }) {
  /*
   * Arriving from a modal's "Find in …" (utils/revealInSearch.js): the
   * reference seeds the filters and the page goes back to 1, both in the FIRST
   * render's state, so the mount search is the one that was asked for. Read
   * before the usePersistedState calls below, which it seeds.
   */
  const reveal = useReveal('search-invoices');
  // Add Credit / Add Debit need what creating an invoice or credit note needs.
  const access = useAccess();
  const { readOnly, reason: readOnlyReason } = useReadOnly();
  const adjustAccess = { canCreate: access.can('invoices.create'), readOnly, readOnlyReason, canNavigate: typeof onNavigate === 'function' };

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedContactId, setSelectedContactId] = usePersistedState('searchInvoices.contactModalId', null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pushingToXero, setPushingToXero] = useState(false);
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);
  const [bulkActionsClosing, setBulkActionsClosing] = useState(false);

  const [selectedInvoiceId, setSelectedInvoiceId] = usePersistedState('searchInvoices.invoiceModalId', null);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDateFrom, setSyncDateFrom] = useState('');
  const [syncDateTo, setSyncDateTo] = useState('');

  const [allocatingCreditNote, setAllocatingCreditNote] = useState(null); // Credit note being allocated
  const [showSyncWarning, setShowSyncWarning] = useState(false);
  const [pendingSyncAction, setPendingSyncAction] = useState(null);

  // Expandable row state
  const [expandedRows, setExpandedRows] = useState(new Set());
  /*
   * Expand All is a MODE, not a count.
   *
   * It used to be inferred from `expandedRows.size === invoices.length`, which
   * is only true until the row set moves underneath it: filter the list and the
   * open rows no longer match the new length, so the button flipped back to
   * "Expand All" over a table that was still expanded, and rows arriving from
   * the new filter came in collapsed. Sorting and paging did the same.
   *
   * Held as a mode and re-applied whenever the rows change — which is what
   * Search Purchase Orders already does, and why that screen stays consistent.
   * Persisted for the same reason it is there: a mode you have to set again
   * every time you navigate away is not a mode.
   */
  const [expandAllMode, setExpandAllMode] = usePersistedState('searchInvoices.expandAll', false);
  const [lineItems, setLineItems] = useState({});
  // Distinct values per filterable column across the whole result set, and the
  // selection the table has made — both owned here because the search is.
  const [facets, setFacets] = useState({});
  const [columnFilterQuery, setColumnFilterQuery] = useState('');
  const [clearColumnFilters, setClearColumnFilters] = useState(0);
  const [loadingLineItems, setLoadingLineItems] = useState({});
  const [expandingAll, setExpandingAll] = useState(false);
  
  const [pagination, setPagination] = usePersistedState('searchInvoices.pagination', {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  }, { seed: reveal && { page: 1 } });

  // merge: a slot saved before a filter key existed (or by the old cross-search,
  // which wrote just { search } here) would otherwise come back missing keys
  // the screen believes it owns. seed: a "Find in …" handoff (see above).
  const [filters, setFilters] = usePersistedState('searchInvoices.filters', {
    search: '',
    status: 'all',
    type: 'all',
    productRange: [],
    datePreset: 'all-time',
    dateFrom: '',
    dateTo: '',
    page: 1,
    limit: '20',
    showVoid: false,  // Filter out void invoices by default
    includeAdditives: true
  }, { merge: true, seed: revealFilters(reveal) });

  const [sortField, setSortField] = usePersistedState('searchInvoices.sortField', 'invoice_date');
  const [sortDirection, setSortDirection] = usePersistedState('searchInvoices.sortDirection', 'desc');
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'info' });
  // A voided invoice / credit note someone tried to edit (VoidedDocumentDialog).
  const [voidedInvoiceId, setVoidedInvoiceId] = useState(null);
  // A till document whose customer is being changed (ChangeCustomerModal).
  const [changingCustomerId, setChangingCustomerId] = useState(null);
  // "Book over…" on an invoice already raised (BookOverInvoiceFlow).
  const [bookingOverId, setBookingOverId] = useState(null);
  const [voidConfirm, setVoidConfirm] = useState({ isOpen: false, invoice: null, isBulk: false });
  const [voiding, setVoiding] = useState(false);
  const [unallocateConfirm, setUnallocateConfirm] = useState({ isOpen: false, invoice: null, errorMsg: '' });
  // When Xero rejects a void because the invoice falls before its year-end
  // lock date, we surface this dialog to let the user (a) cancel and fix
  // Xero, or (b) void locally only and accept the divergence.
  const [lockedConfirm, setLockedConfirm] = useState({ isOpen: false, invoice: null, errorMsg: '', lockDate: null });
  const isInitialMount = useRef(true);

  // Slideout buttons — re-measure when dialogs or bulk actions appear
  useSlideOutButton([bulkActionsVisible, showSyncModal, voidConfirm.isOpen, unallocateConfirm.isOpen, lockedConfirm.isOpen, pushingToXero]);

  useEffect(() => {
    // Back to the page you were on, not page 1: the filters are restored from
    // the nav store, so the page number should be too.
    searchInvoices(pagination.page || 1);
    // Mark initial mount as complete after a short delay to let state settle
    setTimeout(() => {
      isInitialMount.current = false;
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-search on filter change (skip during initial mount)
  useEffect(() => {
    if (isInitialMount.current) return;
    const timeoutId = setTimeout(() => {
      searchInvoices(1);
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  // A column filter is part of the query, so changing one re-runs the search.
  useEffect(() => {
    if (isInitialMount.current) return;
    searchInvoices(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilterQuery]);

  // The search's query string for a page of results — shared by the screen and
  // the export's "all results", so the two cannot filter differently.
  const searchParamsFor = (page, limit) => {
        // Clean up the filters for the API
        const cleanFilters = {
        search: filters.search || '',
        status: filters.status === 'all' ? '' : filters.status,
        type: filters.type === 'all' ? '' : filters.type,
        productRange: productRangeParam(filters.productRange),
        dateFrom: filters.dateFrom || '',
        dateTo: filters.dateTo || '',
        limit: limit || filters.limit || 20,
        showVoid: filters.showVoid ? 'true' : 'false',
        includeAdditives: filters.includeAdditives ? 'true' : 'false'
        };
        
        const queryParams = new URLSearchParams({
        ...cleanFilters,
        page: page,
        sortField: sortField,
        sortDirection: sortDirection
        });
        
        // Column-header filters travel with the search, so they narrow the
        // whole result set and survive paginating and re-searching.
        if (columnFilterQuery) queryParams.set('columnFilters', columnFilterQuery);
        return queryParams;
  };

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const searchInvoices = async (page = 1) => {
    const seq = latest.begin();
    setLoading(true);
    try {
        const queryParams = searchParamsFor(page);

        const response = await fetch(`${API_URL}/api/invoices/search?${queryParams}`, {
        credentials: 'include'
        });
        const data = await response.json();
        if (!latest.isLatest(seq)) return;   // superseded by a later search
        
        if (data.success) {
        setInvoices(data.invoices || []);
        if (data.facets) setFacets(data.facets);
        setPagination({
            page: data.currentPage || page,
            limit: parseInt(filters.limit),
            total: data.total || 0,
            totalPages: data.totalPages || 0
        });
        } else if (data.invoices) {
        // Handle case where success flag might be missing
        setInvoices(data.invoices);
        setPagination({
            page: page,
            limit: parseInt(filters.limit),
            total: data.total || data.invoices.length,
            totalPages: Math.ceil((data.total || data.invoices.length) / parseInt(filters.limit))
        });
        }
    } catch (error) {
        if (!latest.isLatest(seq)) return;
        console.error('Search failed:', error);
        setAlertDialog({ isOpen: true, message: 'Failed to search invoices: ' + error.message, type: 'error' });
    }
    if (!latest.isLatest(seq)) return;
    setLoading(false);
    setSelectedIds(new Set());
    };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    searchInvoices(1);
  };

  /**
   * A plain header click toggles the direction, as it always has. The Customer
   * header's popover passes a direction too, because there it is a choice
   * rather than a toggle — see SortOptionsMenu.
   */
  const handleSort = (field, direction) => {
    if (direction) {
      setSortField(field);
      setSortDirection(direction);
      return;
    }
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  useEffect(() => {
    if (isInitialMount.current) return;
    searchInvoices(pagination.page || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortDirection]);

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      searchInvoices(newPage);
    }
  };

  const syncInvoices = async (syncType = 'custom') => {
    setLoading(true);
    setSyncProgress(0);
    setSyncMessage('Initiating sync...');
    setShowSyncModal(false);

    try {
      let startDate, endDate;
      const today = new Date().toISOString().split('T')[0];

      if (syncType === '3months') {
        startDate = new Date(Date.now() - 90*24*60*60*1000).toISOString().split('T')[0];
        endDate = today;
      } else if (syncType === 'full') {
        startDate = '2020-01-01';
        endDate = today;
      } else {
        // Custom dates from modal
        startDate = syncDateFrom || new Date(Date.now() - 90*24*60*60*1000).toISOString().split('T')[0];
        endDate = syncDateTo || today;
      }

      const eventSource = new EventSource(
        `${API_URL}/api/sync/invoices?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`,
        { withCredentials: true }
      );

      eventSource.onmessage = (event) => {
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          const progress = data.percentage != null
            ? data.percentage
            : data.totalPages > 0
              ? Math.min(95, Math.round((data.currentPage / data.totalPages) * 100))
              : Math.min(95, Math.round((data.currentPage * 100) / Math.max(data.currentPage + 1, 10)));
          setSyncProgress(progress);
          setSyncMessage(data.message || `Fetching page ${data.currentPage}... (${data.recordsFetched} records)`);
        } else if (data.type === 'complete') {
          setSyncProgress(100);
          setSyncMessage('Sync complete!');
          setAlertDialog({ isOpen: true, message: data.result.message || `Synced ${data.result.count} invoices`, type: 'success' });
          searchInvoices(1);
          eventSource.close();

          setTimeout(() => {
            setLoading(false);
            setSyncProgress(0);
            setSyncMessage('');
          }, 1000);
        } else if (data.type === 'error') {
          setAlertDialog({ isOpen: true, message: `Sync failed: ${data.error}`, type: 'error' });
          eventSource.close();
          setLoading(false);
          setSyncProgress(0);
          setSyncMessage('');
        }
      };

      eventSource.onerror = () => {
        setAlertDialog({ isOpen: true, message: 'Connection to server lost during sync', type: 'error' });
        eventSource.close();
        setLoading(false);
        setSyncProgress(0);
        setSyncMessage('');
      };
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to sync: ${error.message}`, type: 'error' });
      setLoading(false);
      setSyncProgress(0);
      setSyncMessage('');
    }
  };

  const openSyncModal = () => {
    // Set default dates for sync modal
    setSyncDateFrom(new Date(Date.now() - 90*24*60*60*1000).toISOString().split('T')[0]);
    setSyncDateTo(new Date().toISOString().split('T')[0]);
    setShowSyncModal(true);
  };

  // eslint-disable-next-line no-unused-vars
  const showContactDetails = (accountId) => {
    setSelectedContactId(accountId);
  };

  const previewInvoice = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
  };

  const voidInvoice = (invoice) => {
    setVoidConfirm({ isOpen: true, invoice, isBulk: false });
  };

  const executeVoid = async () => {
    const { invoice, isBulk } = voidConfirm;
    setVoiding(true);

    try {
      if (isBulk) {
        const response = await fetch(`${API_URL}/api/invoices/bulk-void`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoiceIds: Array.from(selectedIds) })
        });
        const data = await response.json();
        setVoidConfirm({ isOpen: false, invoice: null, isBulk: false });
        setVoiding(false);
        if (data.success) {
          const hasFailures = Array.isArray(data.errors) && data.errors.length > 0;
          // Show warning instead of success if any invoices failed
          let message = data.message;
          if (hasFailures) {
            const lineTotalFails = data.errors.filter(e => e.reason === 'line_total_mismatch');
            if (lineTotalFails.length > 0) {
              const nos = lineTotalFails.map(e => e.invoiceNo).filter(Boolean).join(', ');
              message += `\n\nLine-total discrepancies (left unchanged):\n${nos}`;
            }
          }
          setAlertDialog({ isOpen: true, message, type: hasFailures ? 'warning' : 'success' });
          setSelectedIds(new Set());
          searchInvoices(pagination.page);
        } else {
          setAlertDialog({ isOpen: true, message: data.error || 'Bulk void failed', type: 'error' });
        }
      } else {
        const response = await fetch(`${API_URL}/api/invoices/${invoice.id}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        const data = await response.json();
        setVoidConfirm({ isOpen: false, invoice: null, isBulk: false });
        setVoiding(false);
        if (data.success) {
          setAlertDialog({ isOpen: true, message: data.message, type: 'success' });
          searchInvoices(pagination.page);
        } else if (data.code === 'HAS_ALLOCATIONS') {
          // Offer to unallocate credits first
          setUnallocateConfirm({ isOpen: true, invoice, errorMsg: data.error });
        } else if (data.code === 'XERO_LOCKED') {
          // Year-end lock blocked the Xero side. Offer local-only override.
          setLockedConfirm({ isOpen: true, invoice, errorMsg: data.error, lockDate: data.lockDate || null });
        } else {
          setAlertDialog({ isOpen: true, message: data.error || 'Failed to void invoice', type: 'error' });
        }
      }
    } catch (error) {
      setVoidConfirm({ isOpen: false, invoice: null, isBulk: false });
      setVoiding(false);
      setAlertDialog({ isOpen: true, message: `Failed to void: ${error.message}`, type: 'error' });
    }
  };

  const executeLocalOnlyVoid = async () => {
    const { invoice } = lockedConfirm;
    setLockedConfirm({ isOpen: false, invoice: null, errorMsg: '', lockDate: null });
    setVoiding(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/${invoice.id}?localOnly=true`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();
      setVoiding(false);
      if (data.success) {
        setAlertDialog({
          isOpen: true,
          message: `${invoice.invoice_no || `Invoice #${invoice.id}`} voided locally only. Move the lock date in Xero (Settings → Financial Settings → End of year lock date), void the invoice in Xero manually, then re-lock — until you do, the two systems are out of sync.`,
          type: 'warning'
        });
        searchInvoices(pagination.page);
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to void locally', type: 'error' });
      }
    } catch (error) {
      setVoiding(false);
      setAlertDialog({ isOpen: true, message: `Failed to void locally: ${error.message}`, type: 'error' });
    }
  };

  const executeUnallocateAndVoid = async () => {
    const { invoice } = unallocateConfirm;
    setUnallocateConfirm({ isOpen: false, invoice: null, errorMsg: '' });
    setVoiding(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/${invoice.id}/unallocate-and-void`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      setVoiding(false);
      if (data.success) {
        setAlertDialog({ isOpen: true, message: data.message, type: 'success' });
        searchInvoices(pagination.page);
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Failed to unallocate and void', type: 'error' });
      }
    } catch (error) {
      setVoiding(false);
      setAlertDialog({ isOpen: true, message: `Failed to unallocate and void: ${error.message}`, type: 'error' });
    }
  };

  const toggleSelect = (invoice) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(invoice.id)) {
        next.delete(invoice.id);
      } else {
        next.add(invoice.id);
      }
      return next;
    });
  };

  const dragSelect = useDragSelect({
    setChecked: (id, checked) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (checked) next.add(id); else next.delete(id);
        return next;
      });
    }
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === invoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(invoices.map(i => i.id)));
    }
  };

  /* An invoice is pushable when it has no real Xero document yet. TEMP-/LOCAL-
     prefixed ids are placeholders for something never pushed, so they still
     count (same rule the server applies). */
  const isPushable = (inv) =>
    !['VOIDED', 'DELETED'].includes(inv.status) &&
    (!inv.xero_invoice_id || /^(TEMP|LOCAL)-/i.test(inv.xero_invoice_id));

  const pushableSelected = () =>
    invoices.filter(i => selectedIds.has(i.id) && isPushable(i));

  const bulkPushToXero = async () => {
    const targets = pushableSelected();
    if (targets.length === 0) return;
    setPushingToXero(true);
    try {
      const response = await fetch(`${API_URL}/api/invoices/bulk-push-to-xero`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceIds: targets.map(i => i.id) })
      });
      const data = await response.json();
      if (data.success) {
        const failed = Array.isArray(data.errors) ? data.errors : [];
        let message = `Pushed ${data.pushed} invoice${data.pushed !== 1 ? 's' : ''} to Xero`;
        if (data.skipped) message += `, skipped ${data.skipped} already there`;
        if (failed.length) {
          // Show the reason, not just a count — a Xero period lock and a missing
          // contact both surface here and need completely different fixes.
          const lines = failed.slice(0, 8)
            .map(e => `  ${e.invoiceNo || e.invoiceId}: ${e.error}`).join('\n');
          const more = failed.length > 8 ? `\n  ...and ${failed.length - 8} more` : '';
          message += `\n\n${failed.length} failed:\n${lines}${more}`;
        }
        setAlertDialog({ isOpen: true, message, type: failed.length ? 'warning' : 'success' });
        if (data.pushed) setSelectedIds(new Set());
        searchInvoices(pagination.page);
      } else {
        setAlertDialog({ isOpen: true, message: data.error || 'Push to Xero failed', type: 'error' });
      }
    } catch (err) {
      console.error('Bulk push to Xero error:', err);
      setAlertDialog({ isOpen: true, message: 'Error pushing invoices to Xero', type: 'error' });
    } finally {
      setPushingToXero(false);
    }
  };

  const bulkVoid = () => {
    if (selectedIds.size === 0) return;
    setVoidConfirm({ isOpen: true, invoice: null, isBulk: true });
  };

  // Handle bulk actions bar animation. No clearTimeout cleanup —
  // setBulkActionsClosing(true) re-runs the effect, and cancelling the timer
  // would leave the bar stuck visible.
  useEffect(() => {
    if (selectedIds.size > 0) {
      if (!bulkActionsVisible || bulkActionsClosing) {
        setBulkActionsVisible(true);
        setBulkActionsClosing(false);
      }
    } else if (selectedIds.size === 0 && bulkActionsVisible && !bulkActionsClosing) {
      setBulkActionsClosing(true);
      setTimeout(() => {
        setBulkActionsVisible(false);
        setBulkActionsClosing(false);
      }, 250);
    }
  }, [selectedIds.size, bulkActionsVisible, bulkActionsClosing]);

  /**
   * The invoice list with each invoice's lines beneath it.
   *
   * Lines are fetched here rather than taken from the expanded-row cache, which
   * only holds what has been opened — the export should carry the detail either
   * way.
   */
  const buildExport = async ({ scope = 'page' } = {}) => {
    // "All results" fetches past the page on screen with the same filters and
    // sort, up to the export's own row limit.
    const onPage = invoices;
    const totalAvailable = Math.max(Number(pagination.total) || 0, onPage.length);
    let source = onPage;
    if (scope === 'all' && totalAvailable > onPage.length) {
      try {
        const res = await fetch(`${API_URL}/api/invoices/search?${searchParamsFor(1, EXPORT_ALL_LIMIT)}`,
          { credentials: 'include' });
        const data = await res.json();
        if (Array.isArray(data.invoices)) source = data.invoices;
      } catch (error) {
        console.error('Could not fetch every result for the export:', error);
      }
    }

    let lines = { ...lineItems };
    const missing = source.map((inv) => inv.id).filter((id) => !lines[id]);
    if (missing.length) {
      try {
        const response = await fetch(`${API_URL}/api/invoices/lines/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ ids: missing })
        });
        const data = await response.json();
        lines = { ...lines, ...(data.lines || {}) };
        setLineItems((prev) => ({ ...prev, ...(data.lines || {}) }));
      } catch (error) {
        console.error('Could not load invoice lines for the export:', error);
      }
    }

    const money = (v) => (v === null || v === undefined || v === ''
      ? '' : `£${Number(v).toFixed(2)}`);
    const date = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '');

    const detailColumns = [
      { key: 'code', label: 'Item #', width: '14%' },
      { key: 'description', label: 'Sales description', width: '40%' },
      { key: 'qty', label: 'Qty', width: '9%', numeric: true },
      { key: 'unit', label: 'Unit', width: '12%', numeric: true },
      { key: 'lineTotal', label: 'Line', width: '13%', numeric: true },
      { key: 'account', label: 'Account', width: '12%' }
    ];

    const criteria = [];
    if (filters.search) criteria.push({ label: 'Search', value: filters.search });
    if (filters.status && filters.status !== 'all') criteria.push({ label: 'Status', value: filters.status });
    if (filters.type && filters.type !== 'all') criteria.push({ label: 'Type', value: filters.type });
    if (filters.dateFrom || filters.dateTo) {
      criteria.push({ label: 'Dated', value: `${filters.dateFrom || 'any'} to ${filters.dateTo || 'any'}` });
    }

    return {
      title: 'Invoices',
      kicker: 'INVOICES',
      rowNoun: source.length === 1 ? 'invoice' : 'invoices',
      filename: `invoices-${new Date().toISOString().slice(0, 10)}`,
      landscape: true,
      columns: [
        { key: 'invoiceNo', label: 'Inv #', width: '11%' },
        { key: 'account', label: 'Customer', width: '26%' },
        { key: 'date', label: 'Date', width: '9%' },
        { key: 'dueDate', label: 'Due', width: '9%' },
        { key: 'total', label: 'Total', width: '11%', numeric: true },
        { key: 'paid', label: 'Paid', width: '11%', numeric: true },
        { key: 'balance', label: 'Balance', width: '11%', numeric: true },
        { key: 'status', label: 'Status', width: '12%' }
      ],
      rows: source.map((inv) => ({
        invoiceNo: inv.invoice_no || '',
        account: [inv.account_no, inv.company_name].filter(Boolean).join(' · '),
        date: date(inv.invoice_date),
        dueDate: date(inv.due_date),
        total: money(inv.total),
        paid: money(inv.amount_paid),
        balance: money(inv.balance),
        status: inv.status || '',
        detail: {
          columns: detailColumns,
          emptyLabel: 'No lines on this invoice',
          rows: (lines[inv.id] || []).map((line) => ({
            code: line.item_code || '',
            description: line.description || '',
            qty: formatQuantity(line.quantity),
            unit: line.unit_price === null || line.unit_price === undefined || line.unit_price === ''
              ? '' : formatPrice(line.unit_price),
            lineTotal: money(line.line_total),
            account: line.account_code || ''
          }))
        }
      })),
      totals: [{
        invoiceNo: `${source.length} invoices`,
        total: money(source.reduce((sum, i) => sum + (Number(i.total) || 0), 0)),
        paid: money(source.reduce((sum, i) => sum + (Number(i.amount_paid) || 0), 0)),
        balance: money(source.reduce((sum, i) => sum + (Number(i.balance) || 0), 0))
      }],
      criteria,
      available: { page: onPage.length, all: totalAvailable },
      scopeNote: scope === 'all' && totalAvailable > EXPORT_ALL_LIMIT
        ? `First ${EXPORT_ALL_LIMIT.toLocaleString('en-GB')} of ${totalAvailable.toLocaleString('en-GB')} results`
        : ''
    };
  };

  // getStatusBadgeClass and formatCurrency available in InvoiceTable

  // Print, email and download — one modal for one invoice or a selection.
  const [documentOutput, setDocumentOutput] = useState(null); // { mode, ids }
  const openDocuments = (mode, ids) => {
    const list = Array.from(ids || []);
    if (list.length === 0) return;
    setDocumentOutput({ mode, ids: list });
  };

  // Expand/Collapse All functionality
  const handleExpandAll = async () => {
    if (expandAllMode) {
      setExpandAllMode(false);
      setExpandedRows(new Set());
      return;
    }
    setExpandAllMode(true);
    await expandEveryRow(invoices);
  };

  /** Open every row in the given set, fetching whatever is not already held. */
  const expandEveryRow = useCallback(async (rows) => {
    if (!rows.length) { setExpandedRows(new Set()); return; }

    setExpandingAll(true);
    const allIds = new Set(rows.map(inv => inv.id));

    // Fetch line items for invoices that don't have them yet
    const idsToFetch = rows.filter(inv => !lineItems[inv.id]).map(inv => inv.id);

    if (idsToFetch.length > 0) {
      // Mark all as loading
      const loadingState = {};
      idsToFetch.forEach(id => { loadingState[id] = true; });
      setLoadingLineItems(prev => ({ ...prev, ...loadingState }));

      // Fetch line items in parallel (batched)
      await Promise.all(idsToFetch.map(async (invoiceId) => {
        try {
          // Ensure line items exist in DB
          await fetch(
            `${API_URL}/api/invoices/${invoiceId}/ensure-line-items`,
            { credentials: 'include' }
          );

          // Fetch line items
          const response = await fetch(
            `${API_URL}/api/invoices/${invoiceId}/lines`,
            { credentials: 'include' }
          );
          const data = await response.json();
          setLineItems(prev => ({ ...prev, [invoiceId]: data.lines || [] }));
        } catch (error) {
          console.error('Failed to fetch invoice lines:', error);
          setLineItems(prev => ({ ...prev, [invoiceId]: [] }));
        } finally {
          setLoadingLineItems(prev => ({ ...prev, [invoiceId]: false }));
        }
      }));
    }

    setExpandedRows(allIds);
    setExpandingAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems]);

  /*
   * Re-apply the mode whenever the rows change — a filter, a sort, a page.
   * Without this the button says "Collapse All" over a table that filtered
   * itself back to collapsed, which is the state it claims to be the opposite
   * of.
   */
  useEffect(() => {
    if (!expandAllMode) return;
    if (!invoices.length) return;
    expandEveryRow(invoices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices, expandAllMode]);

  /**
   * Clear every filter on this screen. Named rather than inline because the rep
   * phone shell renders its own reset button, and two copies of this would be
   * two places to remember when a filter is added.
   */
  const handleClearFilters = () => {
    setFilters(prev => ({ ...CLEARED_FILTERS, limit: prev.limit || '20' }));
    setSortField('invoice_date');
    setSortDirection('desc');
    // The column filters live in InvoiceTable; this asks it to drop them,
    // because they are filters and this button says it clears all of them.
    setClearColumnFilters(n => n + 1);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Any filter at all — header or column — lights the Clear Filters button.
  // The column filters live in InvoiceTable; their query string is non-empty
  // exactly when one is on.
  const headerFilters = activeHeaderFilters(filters, CLEARED_FILTERS, { labels: { showVoid: 'Show void' } });
  const filtersApplied = headerFilters.count > 0 || columnFilterQuery !== '';

  return (
    <div className="search-invoices">
      {/* The rep phone shell hides .search-header entirely — see RepMobile.css
          — and this stands in for it. Desktop renders nothing. */}
      <RepMobileFilterReset onReset={handleClearFilters} />
      <div className="search-header">
        <div className="workflow-info" style={{gridColumn: '1'}}>
          <span className="info">Search, add, and manage invoices and credit notes.</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Xero-source invoices are view-only; create/ edit/ void for local-source invoices will sync with Xero. <LastXeroSync entity="invoices" /></span>
          </span>
        </div>
        <div className='search-header-inner' style={{gridColumn: '2'}}>
            <div className="buttons slideout">
              <div>
                <ExportTableButton
                  build={buildExport}
                  user={user}
                  rowCount={invoices.length}
                  onError={(message) => setAlertDialog({ isOpen: true, message, type: 'error' })}
                />
              </div>
              <div>
                <button
                  className={clearFiltersClass(filtersApplied)}
                  onClick={handleClearFilters}
                  title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
                >
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">filter_alt_off</span>
                  </div>
                  <span className="btn-label">Clear Filters</span>
                </button>
              </div>
              <div>
                <button data-write-action onClick={() => onNavigate && onNavigate('create-invoice')} className="btn-slideout" disabled={loading}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">add</span>
                  </div>
                  <span className="btn-label">Add</span>
                </button>
              </div>
              <div>
                <button data-write-action onClick={() => onNavigate && onNavigate('create-credit-note')} className="btn-slideout" disabled={loading}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">credit_card_off</span>
                  </div>
                  <span className="btn-label">Add Credit</span>
                </button>
              </div>
              {user?.is_admin && (<>
              <div>
                <button data-write-action onClick={() => { setPendingSyncAction(() => () => syncInvoices('3months')); setShowSyncWarning(true); }} className="btn-slideout admin-item" disabled={loading}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">sync</span>
                  </div>
                  <span className="btn-label">Sync 3 Months</span>
                </button>
              </div>
              <div>
                <button data-write-action onClick={openSyncModal} className="btn-slideout admin-item" disabled={loading}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">date_range</span>
                  </div>
                  <span className="btn-label">Custom Dates</span>
                </button>
              </div>
              <div>
                <button data-write-action onClick={() => { setPendingSyncAction(() => () => syncInvoices('full')); setShowSyncWarning(true); }} className="btn-slideout admin-item" disabled={loading}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">cloud_download</span>
                  </div>
                  <span className="btn-label">Full Sync</span>
                </button>
              </div>
              </>)}
            </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group inv-filter-search">
            <label>
              Search
              <SearchHelp
                amounts
                fields={['invoice no.', 'reference', 'customer', 'account no.', 'product', 'item code', 'additives', 'total']}
              />
            </label>
            <div className="search-with-additives">
              <Input
                type="text"
                placeholder='Search invoices...'
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                onEnter={() => searchInvoices(1)}
                icon="search"
              />
              <TickCheckbox
                id="invoice-include-additives"
                checked={filters.includeAdditives}
                onChange={(e) => handleFilterChange('includeAdditives', e.target.checked)}
                label="+ADD"
                className="include-additives-pill"
                ariaLabel="Include additives"
              />
            </div>
          </div>

          {/* Status has gone from the row: right-clicking the Status header
              filters on it precisely and across the whole result set. */}
          <div className="filter-group inv-filter-type">
            <label>Type</label>
            <Dropdown
              value={filters.type}
              onChange={(value) => handleFilterChange('type', value)}
              options={[
                { value: 'all', label: 'All Types' },
                { value: 'invoice', label: 'Invoices' },
                { value: 'credit', label: 'Credit Notes' }
              ]}
              placeholder="Select type..."
            />
          </div>

          {/* An invoice is a sale, so its range is the SALES account its lines
              post to. Contact Group has gone: it grouped customers, which the
              Customer header's own filter does across the whole result set. */}
          <ProductRangeFilter
            side="sales"
            value={filters.productRange}
            onChange={(value) => handleFilterChange('productRange', value)}
          />

          <DateRangeFilter
            from={filters.dateFrom}
            to={filters.dateTo}
            preset={filters.datePreset}
            onChange={({ from, to, preset }) => setFilters(prev => ({
              ...prev, dateFrom: from, dateTo: to, datePreset: preset, page: 1
            }))}
          />

          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown
              value={filters.limit}
              onChange={(value) => handleFilterChange('limit', value)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]}
              placeholder="20"
            />
          </div>

          <div className="filter-group">
            <label>Show void</label>
            <div className="checkbox-container">
              <div className="check light">
                <input
                  id="show-void"
                  type="checkbox"
                  checked={filters.showVoid}
                  onChange={(e) => handleFilterChange('showVoid', e.target.checked)}
                />
                <label htmlFor="show-void"></label>
              </div>
            </div>
          </div>
        
        </div>
      </div>

      {syncProgress > 0 && (
        <div className="loading-bar-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} />
        </div>
      )}

      {/* Bulk Actions Bar */}
      {bulkActionsVisible && (
        <div className={`bulk-actions-bar ${bulkActionsClosing ? 'closing' : ''}`}>
          <div className="bulk-actions-info">
            <span className="bulk-actions-title">Bulk Actions:</span>
            <label className="tick-check-label" style={{ gap: '0' }}>
              <input
                type="checkbox"
                className="tick-check-input"
                id="bulk-select-indicator-inv"
                checked={true}
                readOnly
              />
              <label htmlFor="bulk-select-indicator-inv" className="tick-check">
                <svg width="16px" height="16px" viewBox="0 0 18 18">
                  <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                  <polyline points="1 9 7 14 15 4"></polyline>
                </svg>
              </label>
            </label>
            <span>{selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected</span>
          </div>
          <div className="bulk-actions-buttons slideout">
            <div>
              <button className="btn-slideout" onClick={() => openDocuments('download', selectedIds)}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">ios_share</span>
                </div>
                <span className="btn-label">Export Selected</span>
              </button>
            </div>
            {user?.is_admin && (
              <div>
                <button
                  className="btn-slideout"
                  onClick={bulkPushToXero}
                  disabled={pushingToXero || pushableSelected().length === 0}
                  title={pushableSelected().length === 0
                    ? 'None of the selected invoices are waiting to go to Xero'
                    : `Push ${pushableSelected().length} invoice(s) to Xero`}
                >
                  <div className="icon-wrapper">
                    <span className={`material-symbols-rounded ${pushingToXero ? 'spinning' : ''}`}>
                      {pushingToXero ? 'sync' : 'cloud_upload'}
                    </span>
                  </div>
                  <span className="btn-label">
                    {pushingToXero ? 'Pushing...' : `Push ${pushableSelected().length} to Xero`}
                  </span>
                </button>
              </div>
            )}
            <div>
              <button
                className="btn-slideout btn-danger"
                onClick={bulkVoid}
                disabled={invoices.filter(i => selectedIds.has(i.id) && !['VOIDED', 'DELETED', 'PAID'].includes(i.status)).length === 0}
              >
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">block</span>
                </div>
                <span className="btn-label">Void Selected</span>
              </button>
            </div>
            <div>
              <button data-read-safe className="btn-slideout" onClick={() => setSelectedIds(new Set())}>
                <div className="icon-wrapper">
                  <span className="material-symbols-rounded">close</span>
                </div>
                <span className="btn-label">Clear Selection</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="invoices-panel">
        <InvoiceTable
          pagination={pagination}
          toolbarLeft={(
            <button
              className="btn-secondary btn-sm"
              onClick={handleExpandAll}
              disabled={loading || expandingAll || invoices.length === 0}
            >
              <span className="material-symbols-rounded">
                {expandAllMode ? 'unfold_less' : 'unfold_more'}
              </span>
              {expandingAll ? 'Loading...' : (expandAllMode ? 'Collapse All' : 'Expand All')}
            </button>
          )}
          invoices={invoices.map(inv => ({ ...inv, checked: selectedIds.has(inv.id) }))}
          selectedIds={selectedIds}
          bulkActions={(ids) => [
            {
              key: 'bulk-export',
              icon: 'ios_share',
              label: `Export ${ids.size} invoices`,
              onClick: () => openDocuments('download', ids)
            },
            ...(user?.is_admin ? [{
              key: 'bulk-push-xero',
              icon: pushingToXero ? 'sync' : 'cloud_upload',
              label: pushingToXero
                ? 'Pushing...'
                : `Push ${pushableSelected().length} to Xero`,
              disabled: pushingToXero || pushableSelected().length === 0,
              onClick: bulkPushToXero
            }] : []),
            {
              key: 'bulk-void',
              icon: 'block',
              label: `Void ${ids.size} invoices`,
              className: 'action-danger',
              disabled: invoices.filter(i => ids.has(i.id) && !['VOIDED', 'DELETED', 'PAID'].includes(i.status)).length === 0,
              onClick: bulkVoid
            },
            {
              key: 'clear-selection',
              readSafe: true,   // view/filter only — safe for view-only accounts
              icon: 'close',
              label: 'Clear selection',
              onClick: () => setSelectedIds(new Set())
            }
          ]}
          genericActions={[
            {
              key: 'select-all',
              readSafe: true,   // view/filter only — safe for view-only accounts
              icon: 'select_all',
              label: selectedIds.size === invoices.length && invoices.length > 0 ? 'Deselect all' : 'Select all',
              disabled: invoices.length === 0,
              onClick: toggleSelectAll
            },
            {
              key: 'expand-all',
              readSafe: true,   // view/filter only — safe for view-only accounts
              icon: expandAllMode ? 'unfold_less' : 'unfold_more',
              label: expandAllMode ? 'Collapse all' : 'Expand all',
              disabled: loading || expandingAll || invoices.length === 0,
              onClick: handleExpandAll
            },
            {
              key: 'show-void',
              readSafe: true,   // view/filter only — safe for view-only accounts
              icon: 'visibility',
              label: 'Show void invoices',
              active: filters.showVoid,
              onClick: () => handleFilterChange('showVoid', !filters.showVoid)
            }
          ]}
          columns={['checkbox', 'invoiceNo', 'account', 'date', 'dueDate', 'total', 'amount_paid', 'balance', 'status']}
          actions={[
            {
              type: 'checkbox',
              handler: toggleSelect,
              headerHandler: toggleSelectAll,
              allChecked: selectedIds.size === invoices.length && invoices.length > 0,
              onMouseDown: (inv) => dragSelect.onMouseDown(inv.id, selectedIds.has(inv.id)),
              onMouseEnter: (inv) => dragSelect.onMouseEnter(inv.id)
            },
            {
              type: 'preview',
              label: 'Preview',
              icon: 'visibility',
              handler: (invoice) => previewInvoice(invoice.id)
            },
            {
              type: 'edit',
              label: 'Edit',
              icon: 'edit',
              handler: (invoice) => {
                // Voided: explain, and offer a copy or an email to accounts.
                if (isVoided(invoice.status)) { setVoidedInvoiceId(invoice.id); return; }
                sessionStorage.setItem('editInvoiceId', invoice.id.toString());
                onNavigate && onNavigate(invoice.is_credit ? 'create-credit-note' : 'create-invoice');
              },
              // The server's reason (null = editable): local documents edit at
              // any status unless paid, allocated or in a locked Xero period.
              // A voided one stays live and explains itself instead.
              isDisabled: (invoice) => !!invoice.edit_block && !isVoided(invoice.status),
              disabledTitle: (invoice) => invoice.edit_block,
              title: (invoice) => (isVoided(invoice.status)
                ? `Voided — it cannot be edited or restored. Raise a copy, or email accounts`
                : 'Edit')
            },
            // A till document's customer — the one change a till document
            // allows (here and in Xero). On every row so the menu is the same
            // length everywhere; the server's reason disables it elsewhere.
            {
              type: 'change-customer',
              label: 'Change customer…',
              icon: 'swap_horiz',
              handler: (invoice) => setChangingCustomerId(invoice.id),
              isDisabled: (invoice) => !!invoice.customer_change_block,
              disabledTitle: (invoice) => invoice.customer_change_block,
              title: 'Move this till document to the right customer, here and in Xero'
            },
            // Book over: move an unpaid invoice to another date, or split it
            // over several (services/invoiceBookOverService). On every row,
            // greyed with the server's reason where it does not apply.
            {
              type: 'book-over',
              label: 'Book over…',
              icon: 'event_repeat',
              handler: (invoice) => setBookingOverId(invoice.id),
              isDisabled: (invoice) => !!invoice.book_over_block,
              disabledTitle: (invoice) => invoice.book_over_block,
              title: 'Move this invoice to another date, or split it over several — while it is unpaid'
            },
            // Export: preview, then print, download or email to yourself —
            // one modal for one invoice or many (DocumentOutputModal).
            {
              type: 'export',
              label: 'Export',
              icon: 'ios_share',
              handler: (invoice) => openDocuments('download', [invoice.id]),
              isDisabled: (invoice) => ['VOIDED', 'DELETED'].includes(invoice.status),
              disabledTitle: 'Cannot export — invoice is voided or deleted'
            },
            {
              type: 'void',
              label: 'Void',
              icon: 'block',
              className: 'btn-danger',
              handler: voidInvoice,
              isDisabled: (invoice) => ['VOIDED', 'DELETED', 'PAID'].includes(invoice.status),
              disabledTitle: 'Cannot void — invoice is voided, deleted, or paid'
            },
            {
              type: 'allocate',
              label: 'Allocate Credit',
              icon: 'credit_score',
              handler: (invoice) => setAllocatingCreditNote(invoice),
              isDisabled: (invoice) => !(invoice.is_credit || !invoice.invoice_no?.startsWith('CN-')) && !['VOIDED', 'DELETED', 'PAID'].includes(invoice.status)
            },
            // A credit note / invoice pre-filled from this invoice, linked to
            // it and (a credit) allocated to it on save. On every row, disabled
            // with the reason where it does not apply.
            ...['credit', 'debit'].map(kind => ({
              type: `add-${kind}`,
              label: ADJUST_ACTIONS[kind].label,
              icon: ADJUST_ACTIONS[kind].icon,
              handler: (invoice) => startAdjustment(onNavigate, invoice.id, kind),
              isDisabled: (invoice) => !!adjustActionBlock(kind, invoice, adjustAccess),
              disabledTitle: (invoice) => adjustActionBlock(kind, invoice, adjustAccess),
              title: ADJUST_ACTIONS[kind].title
            }))
          ]}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onPageChange={changePage}
          loading={loading}
          expandable={true}
          showSettlement={true}
          expandedRows={expandedRows}
          onExpandedRowsChange={setExpandedRows}
          lineItems={lineItems}
          facets={facets}
          columnFilterKey="searchInvoices.columnFilters"
          onColumnFiltersChange={() => setPagination(prev => ({ ...prev, page: 1 }))}
          onColumnFiltersQuery={setColumnFilterQuery}
          clearColumnFiltersSignal={clearColumnFilters}
          headerFilters={headerFilters}
          onClearFilters={handleClearFilters}
          onLineItemsChange={setLineItems}
          loadingLineItems={loadingLineItems}
          onLoadingLineItemsChange={setLoadingLineItems}
          onInvoiceClick={(invoice) => previewInvoice(invoice.id)}
          onContactClick={(accountId) => setSelectedContactId(accountId)}
        />

      </div>

      {selectedContactId && ReactDOM.createPortal(
        <ContactModal 
          contactId={selectedContactId}
          onClose={() => setSelectedContactId(null)}
        />,
        document.body
      )}

      {voidedInvoiceId && (
        <VoidedDocumentDialog
          kind="invoice"
          id={voidedInvoiceId}
          onClose={() => { setVoidedInvoiceId(null); searchInvoices(pagination.page || 1); }}
          onNavigate={onNavigate}
          onSent={(message) => setAlertDialog({ isOpen: true, type: 'success', message })}
        />
      )}

      {changingCustomerId && (
        <ChangeCustomerModal
          invoiceId={changingCustomerId}
          onClose={() => { setChangingCustomerId(null); searchInvoices(pagination.page || 1); }}
          onDone={(result) => {
            setChangingCustomerId(null);
            searchInvoices(pagination.page || 1);
            setAlertDialog({ isOpen: true, type: 'success',
              message: `${result.invoice?.invoice_no || 'The document'}: ${describeCustomerChange(result.change)}.` });
          }}
        />
      )}

      {bookingOverId && (
        <BookOverInvoiceFlow
          invoiceId={bookingOverId}
          onDone={() => searchInvoices(pagination.page || 1)}
          onClose={() => { setBookingOverId(null); searchInvoices(pagination.page || 1); }}
        />
      )}

      {selectedInvoiceId && (
        <InvoiceModal
          invoiceId={selectedInvoiceId}
          onClose={() => { setSelectedInvoiceId(null); searchInvoices(pagination.page); }}
        />
      )}

      {allocatingCreditNote && (
        <AllocateCreditNoteModal
          isOpen={true}
          creditNote={allocatingCreditNote}
          onClose={() => setAllocatingCreditNote(null)}
          onSuccess={() => {
            setAllocatingCreditNote(null);
            searchInvoices(pagination.page);
          }}
        />
      )}

      {/* Custom Date Sync Modal */}
      {showSyncModal && createPortal(
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal-content modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: '0' }}>Sync Invoices - Custom Dates</h3>
              <button className="btn-icon" onClick={() => setShowSyncModal(false)}>
                <span className="material-symbols-rounded">close</span>
              </button>
            </div>
            <div className="modal-body" style={{ padding: 'var(--spacing-lg) var(--spacing-xl) var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
              <div className="form-group">
                <label>From Date</label>
                <DateInput
                  value={syncDateFrom}
                  onChange={(e) => setSyncDateFrom(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>To Date</label>
                <DateInput
                  value={syncDateTo}
                  onChange={(e) => setSyncDateTo(e.target.value)}
                />
              </div>
            </div>
              <div className="modal-footer">
                <button data-read-safe className="btn-round btn-danger" onClick={() => setShowSyncModal(false)}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">close</span>
                  </div>
                  <span className="btn-label">Cancel</span>
                </button>
                <button className="btn-slideout" onClick={() => syncInvoices('custom')}>
                  <div className="icon-wrapper">
                    <span className="material-symbols-rounded">sync</span>
                  </div>
                  <span className="btn-label">Start Sync</span>
                </button>
              </div>
          </div>
        </div>, document.body
      )}

      {/* Void Confirmation Dialog */}
      <ConfirmDialog
        isOpen={voidConfirm.isOpen}
        title={voidConfirm.isBulk ? `Void ${selectedIds.size} Invoice${selectedIds.size !== 1 ? 's' : ''}` : `Void ${voidConfirm.invoice?.invoice_no}`}
        message={voiding
          ? 'Voiding...'
          : (voidConfirm.isBulk
            ? `Are you sure you want to void ${selectedIds.size} invoice${selectedIds.size !== 1 ? 's' : ''}? This will also void them in Xero if synced.`
            : `Are you sure you want to void ${voidConfirm.invoice?.invoice_no}? This will also void it in Xero if synced.`
          )
        }
        confirmText={voiding ? 'Voiding...' : 'Void'}
        type="error"
        onConfirm={executeVoid}
        onCancel={() => setVoidConfirm({ isOpen: false, invoice: null, isBulk: false })}
      />

      {/* Unallocate & Void Confirmation */}
      <ConfirmDialog
        isOpen={unallocateConfirm.isOpen}
        title="Credit Allocations Exist"
        message={voiding
          ? 'Removing credit allocations and voiding...'
          : `${unallocateConfirm.invoice?.invoice_no} has credit notes allocated against it and cannot be voided directly.\n\nWould you like to automatically remove the credit allocations in Xero and then void the invoice?`
        }
        confirmText={voiding ? 'Unallocating...' : 'Unallocate & Void'}
        type="warning"
        onConfirm={executeUnallocateAndVoid}
        onCancel={() => setUnallocateConfirm({ isOpen: false, invoice: null, errorMsg: '' })}
      />

      {/* Xero locked — offer local-only void */}
      <ConfirmDialog
        isOpen={lockedConfirm.isOpen}
        title="Xero Year-End Lock Blocking Void"
        message={voiding
          ? 'Voiding locally only…'
          : `Xero rejected the void on ${lockedConfirm.invoice?.invoice_no || 'this invoice'}${lockedConfirm.lockDate ? ` because it's dated before Xero's year-end lock (${lockedConfirm.lockDate})` : ' because of a year-end lock'}.\n\nTo fix Xero too, move the lock date in Xero (Settings → Financial Settings → End of year lock date), void the invoice in Xero manually, then re-lock.\n\nAlternatively, void here only — the invoice will show as VOIDED in this system but will still be AUTHORISED in Xero until you sort out the lock. The two will stay out of sync until then.`
        }
        confirmText={voiding ? 'Voiding…' : 'Void Locally Only'}
        cancelText="Cancel"
        type="warning"
        onConfirm={executeLocalOnlyVoid}
        onCancel={() => setLockedConfirm({ isOpen: false, invoice: null, errorMsg: '', lockDate: null })}
      />

      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />

      <ConfirmDialog
        isOpen={showSyncWarning}
        title="Sync Invoices from Xero"
        message="This will overwrite all local invoice data with the latest from Xero for the period being synced. Any local changes not yet pushed to Xero will be lost."
        type="warning"
        confirmText="Sync"
        onConfirm={() => { setShowSyncWarning(false); pendingSyncAction?.(); }}
        onCancel={() => setShowSyncWarning(false)}
      />

      {documentOutput && (
        <DocumentOutputModal
          type="invoice"
          ids={documentOutput.ids}
          initialMode={documentOutput.mode}
          user={user}
          onClose={() => setDocumentOutput(null)}
          onDone={(message) => {
            setDocumentOutput(null);
            if (message) setAlertDialog({ isOpen: true, message, type: 'success' });
          }}
        />
      )}
    </div>
  );
}

export default SearchInvoices;